import dayjs from 'dayjs';
import { useMemo } from 'react';

import {
    useSearchAddonsQuery,
    useSearchDatasetsQuery,
    useSearchSubscriptionsQuery,
    useSearchSupportPackagesQuery,
    useSearchUsersQuery,
    useSearchWorkspacesQuery,
} from 'src/apiClients';
import type {
    AddonSearchItem,
    DatasetListItem,
    QueryWhereValue,
    SupportPackageItem,
    Subscription,
    UserListItem,
    WorkspaceListItem,
} from 'src/apiClients/queries';
import { SEARCH_RESULT_LIMIT } from 'src/apiClients/queries';
import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';
import { useDebouncedValue } from 'src/hooks/use-debounced-value';
import type { EntityStatusTone } from 'src/pages/instance-detail-page/entity-status-tone';
import { ROUTES } from 'src/utils/strings';
import { ADDON_PRODUCT_NAMES } from 'src/utils/table-formatters';

import { TOP_BAR_SEARCH_GROUP_ORDER } from './entity-types';
import type { GlobalSearchFilters, TopBarSearchEntityType } from './entity-types';

/** Minimum characters before the typeahead fans out a query. */
export const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

// Re-exported so existing search-feature consumers can keep importing from here.
export { TOP_BAR_SEARCH_GROUP_ORDER };
export type { GlobalSearchFilters, TopBarSearchEntityType };

export interface TopBarSearchStatus {
    label: string;
    tone: EntityStatusTone;
}

export interface TopBarSearchResult {
    /** Stable key, unique across types (`${type}:${id}`). */
    key: string;
    type: TopBarSearchEntityType;
    primary: string;
    secondary?: string;
    tertiary?: string;
    status?: TopBarSearchStatus;
    badges?: string[];
    avatarLabel?: string;
    /** Destination passed to `navigate` when the result is selected. */
    to: string;
    /** Section id to scroll to on the instance detail page after navigation. */
    scrollTo?: string;
    /** Row id to highlight in the destination table after navigation. */
    highlightId?: string;
    /**
     * Raw field values (e.g. `state`, `type`, `isSubscriptionOwner`) that the "All Filters"
     * tab can match against. An array value (e.g. an instance's addon product codes) matches
     * when ANY entry is in the active selection.
     */
    filterableFields?: Record<string, DataTableCellValue | DataTableCellValue[]>;
}

/**
 * Selections made in the "All Filters" tab, keyed by `${entityType}.${field}`
 * (e.g. `instance.state`, `user.type`). `undefined` for a key means no filter
 * is applied for that field — mirrors `PageFilterRegistration.filters`.
 */

export interface TopBarSearchGroup {
    type: TopBarSearchEntityType;
    results: TopBarSearchResult[];
    /** More matches exist on the server than the group displays — show a truncation hint. */
    hasMore: boolean;
}

/**
 * Filterable fields that map to a REAL server column per entity type and so can
 * be pushed into the search query's `where`. Synthetic fields (instance
 * `productCode`/`name`, derived from addons) are intentionally excluded and stay
 * client-side in `matchesGlobalFilters`.
 */
const SERVER_FILTER_FIELDS: Record<TopBarSearchEntityType, readonly string[]> = {
    instance: ['state'],
    user: ['state', 'type', 'isSubscriptionOwner'],
    addon: ['state'],
    workspace: ['state', 'access'],
    dataset: ['state'],
    supportPackage: ['state'],
};

/**
 * Translates the active "All Filters" selections into a server-side `where`
 * fragment for one entity type. Only real server columns are forwarded; a field
 * is skipped when its selection is undefined or contains empty/null values
 * (mirrors `useEntityListQueryState`). Single-value selections pass a scalar;
 * multi-value selections pass the array ($in).
 */
const buildServerFilterWhere = (
    type: TopBarSearchEntityType,
    globalFilters: GlobalSearchFilters | undefined,
): Record<string, QueryWhereValue> | undefined => {
    if (!globalFilters) return undefined;
    const where: Record<string, QueryWhereValue> = {};
    for (const field of SERVER_FILTER_FIELDS[type]) {
        const values = globalFilters[`${type}.${field}`];
        if (
            values === undefined ||
            values.length === 0 ||
            values.some((v) => v === '' || v == null)
        )
            continue;
        where[field] = values.length === 1 ? (values[0] as QueryWhereValue) : values;
    }
    return Object.keys(where).length > 0 ? where : undefined;
};

export interface UseTopBarSearchResult {
    /** Non-empty groups in fixed order. */
    groups: TopBarSearchGroup[];
    /** A flat, render-order list of every result (for keyboard navigation). */
    flatResults: TopBarSearchResult[];
    isFetching: boolean;
    /** The debounced term met the minimum length, so a query was issued. */
    hasQuery: boolean;
    totalResults: number;
}

const STATE_TO_TONE: Record<string, EntityStatusTone> = {
    A: 'active',
    I: 'inactive',
    X: 'archived',
};

const STATE_TO_LABEL: Record<string, string> = {
    A: 'Active',
    I: 'Inactive',
    X: 'Archived',
};

const toStatus = (state: string): TopBarSearchStatus | undefined => {
    const tone = STATE_TO_TONE[state];
    const label = STATE_TO_LABEL[state];
    return tone && label ? { tone, label } : undefined;
};

const toAvatarLabel = (value: string): string =>
    value
        .split(/[\s._@-]+/)
        .slice(0, 2)
        .map((s) => s[0]?.toUpperCase() ?? '')
        .join('');

const toInstanceResult = (sub: Subscription): TopBarSearchResult => {
    const primary = sub.subscriptionName || sub.customerName || sub.id;
    const secondary =
        sub.customerName && sub.customerName !== primary
            ? sub.customerName
            : sub.companyName || undefined;
    const badges: string[] = [];
    if (sub.addons.length > 0)
        badges.push(`${sub.addons.length} add-on${sub.addons.length === 1 ? '' : 's'}`);
    return {
        key: `instance:${sub.id}`,
        type: 'instance',
        primary,
        secondary,
        tertiary: sub.tenantId || undefined,
        status: toStatus(sub.state),
        badges: badges.length > 0 ? badges : undefined,
        to: `${ROUTES.INSTANCES}/${sub.id}`,
        // Land on the details section even when already scrolled deep into
        // the same instance's page.
        scrollTo: 'details',
        filterableFields: {
            state: sub.state,
            productCode: sub.addons.map((addon) => addon.name),
            name: sub.addons.map((addon) => ADDON_PRODUCT_NAMES[addon.name] ?? addon.name),
        },
    };
};

const toUserResult = (user: UserListItem): TopBarSearchResult => {
    const primary = user.login || user.email || user.id;
    const badges: string[] = [];
    if (user.isSubscriptionOwner) badges.push('Owner');
    if (user.type) badges.push(user.type);
    return {
        key: `user:${user.id}`,
        type: 'user',
        primary,
        secondary: user.email && user.email !== primary ? user.email : undefined,
        status: toStatus(user.state),
        badges: badges.length > 0 ? badges : undefined,
        avatarLabel: toAvatarLabel(primary),
        to: ROUTES.USERS,
        highlightId: user.id,
        filterableFields: {
            state: user.state,
            type: user.type,
            isSubscriptionOwner: user.isSubscriptionOwner,
        },
    };
};

const toSupportPackageResult = (pkg: SupportPackageItem): TopBarSearchResult => {
    const created =
        pkg.createdAt && dayjs(pkg.createdAt).isValid()
            ? dayjs(pkg.createdAt).format('D MMM YYYY')
            : undefined;
    return {
        key: `supportPackage:${pkg.id}`,
        type: 'supportPackage',
        primary: pkg.message || pkg.id,
        secondary: created,
        tertiary: pkg.id,
        status: toStatus(pkg.state),
        to: ROUTES.SUPPORT_PACKAGES,
        highlightId: pkg.id,
        filterableFields: { state: pkg.state },
    };
};

const toWorkspaceResult = (ws: WorkspaceListItem, instanceId?: string): TopBarSearchResult => ({
    key: `workspace:${ws.id}`,
    type: 'workspace',
    primary: ws.name || ws.id,
    secondary: ws.description || undefined,
    tertiary: ws.id,
    status: toStatus(ws.state),
    badges: [ws.access === 'public' ? 'Public' : 'Private'],
    to: instanceId ? `${ROUTES.INSTANCES}/${instanceId}` : ROUTES.INSTANCES,
    scrollTo: instanceId ? 'workspaces' : undefined,
    highlightId: ws.id,
    filterableFields: { state: ws.state, access: ws.access },
});

const toDatasetResult = (ds: DatasetListItem): TopBarSearchResult => ({
    key: `dataset:${ds.id}`,
    type: 'dataset',
    primary: ds.name || ds.id,
    secondary: ds.description || undefined,
    tertiary: ds.id,
    status: toStatus(ds.state),
    to: ds.subscriptionId ? `${ROUTES.INSTANCES}/${ds.subscriptionId}` : ROUTES.INSTANCES,
    scrollTo: ds.subscriptionId ? 'workspaces' : undefined,
    // Datasets aren't a row in the destination table; highlight the owning
    // workspace row so the navigation lands on something visible.
    highlightId: ds.subscriptionId ? ds.workspaceId || undefined : undefined,
    filterableFields: { state: ds.state },
});

const toAddonResult = (addon: AddonSearchItem): TopBarSearchResult => {
    const productName = ADDON_PRODUCT_NAMES[addon.name] ?? addon.name;
    const badges: string[] = [];
    if (addon.type) badges.push(addon.type);
    if (addon.productLevel) badges.push(addon.productLevel);
    return {
        key: `addon:${addon.id}`,
        type: 'addon',
        primary: productName,
        secondary: addon.salesforceQuoteNumber || undefined,
        tertiary: addon.id,
        status: toStatus(addon.state),
        badges: badges.length > 0 ? badges : undefined,
        avatarLabel: toAvatarLabel(productName),
        to: addon.subscriptionId ? `${ROUTES.INSTANCES}/${addon.subscriptionId}` : ROUTES.INSTANCES,
        scrollTo: addon.subscriptionId ? 'subscriptions' : undefined,
        highlightId: addon.id,
        filterableFields: { state: addon.state },
    };
};

/**
 * Drives the global TopBar typeahead: debounces the raw input, fans out to the
 * instances, users, and support-packages search endpoints in parallel, and
 * projects the responses into a fixed-order list of grouped results.
 */
/** A result passes when every one of its filterable fields matches the active selection (or has none). */
const matchesGlobalFilters = (
    type: TopBarSearchEntityType,
    result: TopBarSearchResult,
    globalFilters: GlobalSearchFilters | undefined,
): boolean => {
    if (!globalFilters || !result.filterableFields) return true;
    return Object.entries(result.filterableFields).every(([field, value]) => {
        const selected = globalFilters[`${type}.${field}`];
        if (selected === undefined) return true;
        return Array.isArray(value)
            ? value.length === 0 || value.some((v) => selected.includes(v))
            : selected.includes(value);
    });
};

export const useTopBarSearch = (
    rawTerm: string,
    tenantId?: string,
    instanceId?: string,
    globalFilters?: GlobalSearchFilters,
): UseTopBarSearchResult => {
    const term = useDebouncedValue(rawTerm.trim(), SEARCH_DEBOUNCE_MS);
    const hasQuery = term.length >= MIN_SEARCH_LENGTH;
    const skip = !hasQuery;

    const instances = useSearchSubscriptionsQuery(
        { term, where: buildServerFilterWhere('instance', globalFilters) },
        { skip },
    );
    const addons = useSearchAddonsQuery(
        { term, where: buildServerFilterWhere('addon', globalFilters) },
        { skip },
    );
    const users = useSearchUsersQuery(
        { term, where: buildServerFilterWhere('user', globalFilters) },
        { skip },
    );
    const supportPackages = useSearchSupportPackagesQuery(
        { term, where: buildServerFilterWhere('supportPackage', globalFilters) },
        { skip },
    );
    const workspaces = useSearchWorkspacesQuery(
        {
            term,
            tenantId,
            subscriptionId: instanceId,
            where: buildServerFilterWhere('workspace', globalFilters),
        },
        { skip: skip || !tenantId },
    );
    const datasets = useSearchDatasetsQuery(
        {
            term,
            tenantId,
            subscriptionId: instanceId,
            where: buildServerFilterWhere('dataset', globalFilters),
        },
        { skip: skip || !tenantId },
    );

    const groups = useMemo<TopBarSearchGroup[]>(() => {
        if (!hasQuery) return [];
        const activeTypes: readonly TopBarSearchEntityType[] = tenantId
            ? TOP_BAR_SEARCH_GROUP_ORDER
            : TOP_BAR_SEARCH_GROUP_ORDER.filter((t) => t !== 'workspace' && t !== 'dataset');
        const byType: Record<TopBarSearchEntityType, TopBarSearchResult[]> = {
            instance: (instances.data ?? []).map(toInstanceResult),
            addon: (addons.data ?? []).map(toAddonResult),
            workspace: (workspaces.data ?? []).map((ws) => toWorkspaceResult(ws, instanceId)),
            dataset: (datasets.data ?? []).map(toDatasetResult),
            user: (users.data ?? []).map(toUserResult),
            supportPackage: (supportPackages.data ?? []).map(toSupportPackageResult),
        };
        return activeTypes
            .map((type) => {
                const matched = byType[type].filter((result) =>
                    matchesGlobalFilters(type, result, globalFilters),
                );
                return {
                    type,
                    results: matched.slice(0, SEARCH_RESULT_LIMIT),
                    hasMore: matched.length > SEARCH_RESULT_LIMIT,
                };
            })
            .sort((a, b) => (a.results.length > 0 ? 0 : 1) - (b.results.length > 0 ? 0 : 1));
    }, [
        hasQuery,
        tenantId,
        instanceId,
        globalFilters,
        instances.data,
        addons.data,
        workspaces.data,
        datasets.data,
        users.data,
        supportPackages.data,
    ]);

    const flatResults = useMemo(() => groups.flatMap((g) => g.results), [groups]);

    return {
        groups,
        flatResults,
        isFetching:
            instances.isFetching ||
            addons.isFetching ||
            users.isFetching ||
            supportPackages.isFetching ||
            (!!tenantId && (workspaces.isFetching || datasets.isFetching)),
        hasQuery,
        totalResults: flatResults.length,
    };
};
